/**
 * StudioColumn - Dark theme right panel matching NotebookLM design
 *
 * Features:
 * - Dark glass morphism design
 * - 2-column grid of template cards (Audio, Mindmap, Flashcards, etc.)
 * - Each card has icon + label + edit button
 * - Saved notes section at bottom
 */

import { useState } from 'react';
import { SavedNotesList, SavedNote } from './SavedNotesList';

// === ICONS ===

// Panel toggle icon for header
const PanelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

// Template card icons
const AudioIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const VideoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="17" x2="22" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
  </svg>
);

const MindmapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <circle cx="4" cy="6" r="2" />
    <circle cx="20" cy="6" r="2" />
    <circle cx="4" cy="18" r="2" />
    <circle cx="20" cy="18" r="2" />
    <line x1="9.5" y1="10" x2="5.5" y2="7.5" />
    <line x1="14.5" y1="10" x2="18.5" y2="7.5" />
    <line x1="9.5" y1="14" x2="5.5" y2="16.5" />
    <line x1="14.5" y1="14" x2="18.5" y2="16.5" />
  </svg>
);

const ReportIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const FlashcardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const QuizIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfographicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const PresentationIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const DataTableIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const NoteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// === TEMPLATE CARD DATA ===
interface TemplateCard {
  id: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const TEMPLATE_CARDS: TemplateCard[] = [
  { id: 'audio', label: 'Audio...', icon: <AudioIcon /> },
  { id: 'video', label: 'Video Overview', icon: <VideoIcon /> },
  { id: 'mindmap', label: 'Mindmap', icon: <MindmapIcon /> },
  { id: 'reports', label: 'Reports', icon: <ReportIcon /> },
  { id: 'flashcards', label: 'Flashcards', icon: <FlashcardIcon /> },
  { id: 'quiz', label: 'Quiz', icon: <QuizIcon /> },
  { id: 'infographic', label: 'Infographic', icon: <InfographicIcon /> },
  { id: 'presentation', label: 'Presentation', icon: <PresentationIcon /> },
  { id: 'datatable', label: 'Data Table', icon: <DataTableIcon />, disabled: true },
];

// === COMPONENT ===
interface StudioColumnProps {
  spaceId: string;
  notes: SavedNote[];
  isLoading: boolean;
  onCreateNote: (title: string, content: string) => Promise<void>;
  onUpdateNote: (noteId: string, title: string, content: string) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  onGenerateSummary: () => Promise<string | null>;
  isGeneratingSummary?: boolean;
}

export function StudioColumn({
  notes,
  isLoading,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  onGenerateSummary,
  isGeneratingSummary = false,
}: StudioColumnProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Handle creating a new note
  const handleAddNote = async () => {
    if (!newNoteTitle.trim() && !newNoteContent.trim()) return;

    setIsSaving(true);
    try {
      await onCreateNote(
        newNoteTitle.trim() || 'Untitled Note',
        newNoteContent.trim()
      );
      setNewNoteTitle('');
      setNewNoteContent('');
      setIsAddingNote(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle generating summary and saving as note
  const handleGenerateSummary = async () => {
    const summary = await onGenerateSummary();
    if (summary) {
      await onCreateNote('Summary', summary);
    }
  };

  // Handle template card click
  const handleTemplateClick = (templateId: string) => {
    // For now, only Summary is functional
    if (templateId === 'reports') {
      handleGenerateSummary();
    }
    // Other templates would be implemented here
  };

  return (
    <div className="studio-column">
      {/* Header */}
      <div className="studio-header">
        <h2 className="studio-title">Studio</h2>
        <button type="button" className="panel-toggle" title="Toggle panel">
          <PanelIcon />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="studio-scroll-area">
        {/* Template Cards Grid */}
        <div className="templates-grid">
          {TEMPLATE_CARDS.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`template-card ${card.disabled ? 'disabled' : ''}`}
              onClick={() => !card.disabled && handleTemplateClick(card.id)}
              disabled={card.disabled || (card.id === 'reports' && isGeneratingSummary)}
            >
              <div className="card-icon">{card.icon}</div>
              <span className="card-label">
                {card.id === 'reports' && isGeneratingSummary ? 'Generating...' : card.label}
              </span>
              {!card.disabled && (
                <button
                  type="button"
                  className="card-edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateClick(card.id);
                  }}
                >
                  <EditIcon />
                </button>
              )}
            </button>
          ))}
        </div>

        {/* Saved Notes Section */}
        <div className="notes-section">
          <div className="notes-header">
            <div className="notes-title-row">
              <NoteIcon />
              <span className="notes-title">
                {notes.length > 0
                  ? `${notes[0]?.title || 'Saved Note'}`
                  : 'No saved notes'}
              </span>
            </div>
            {notes.length > 0 && (
              <span className="notes-time">
                {notes[0]?.createdAt
                  ? `${formatTimeAgo(new Date(notes[0].createdAt))}`
                  : ''}
              </span>
            )}
          </div>

          {/* Add Note Button */}
          <button
            type="button"
            className="add-note-btn"
            onClick={() => setIsAddingNote(true)}
          >
            <PlusIcon />
            <span>Add Note</span>
          </button>

          {/* Add Note Form */}
          {isAddingNote && (
            <div className="add-note-form">
              <input
                type="text"
                className="note-title-input"
                placeholder="Note title..."
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                autoFocus
              />
              <textarea
                className="note-content-input"
                placeholder="Write your note..."
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={4}
              />
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setIsAddingNote(false);
                    setNewNoteTitle('');
                    setNewNoteContent('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="save-btn"
                  onClick={handleAddNote}
                  disabled={isSaving || (!newNoteTitle.trim() && !newNoteContent.trim())}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 && (
            <div className="notes-list-container">
              <SavedNotesList
                notes={notes}
                isLoading={isLoading}
                onUpdate={onUpdateNote}
                onDelete={onDeleteNote}
              />
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* === BASE LAYOUT - DARK THEME === */
        .studio-column {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #0A0A0C;
          overflow: hidden;
        }

        /* === HEADER === */
        .studio-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: rgba(10, 10, 12, 0.8);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .studio-title {
          font-size: 16px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }

        .panel-toggle {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: rgba(148, 163, 184, 0.5);
          cursor: pointer;
          transition: all 0.15s;
        }

        .panel-toggle:hover {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(148, 163, 184, 0.8);
        }

        /* === SCROLLABLE AREA === */
        .studio-scroll-area {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
        }

        /* Custom scrollbar */
        .studio-scroll-area::-webkit-scrollbar {
          width: 6px;
        }
        .studio-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }
        .studio-scroll-area::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }

        /* === TEMPLATE CARDS GRID === */
        .templates-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-bottom: 32px;
        }

        .template-card {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 10px;
          background: #1a1a1f;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s ease;
          position: relative;
          text-align: left;
          overflow: hidden;
          min-width: 0;
        }

        .template-card:hover:not(.disabled) {
          background: #222228;
          border-color: rgba(255, 255, 255, 0.15);
        }

        .template-card.disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(148, 163, 184, 0.7);
          flex-shrink: 0;
        }

        .template-card:hover:not(.disabled) .card-icon {
          color: rgba(148, 163, 184, 0.9);
        }

        .card-label {
          flex: 1;
          font-size: 12px;
          font-weight: 500;
          min-width: 0;
          color: rgba(226, 232, 240, 0.85);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .template-card:hover:not(.disabled) .card-label {
          color: #fff;
        }

        .card-edit {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          border: none;
          border-radius: 6px;
          color: rgba(148, 163, 184, 0.5);
          cursor: pointer;
          opacity: 0;
          transition: all 0.15s;
          flex-shrink: 0;
        }

        .template-card:hover .card-edit {
          opacity: 1;
        }

        .card-edit:hover {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(148, 163, 184, 0.9);
        }

        /* === NOTES SECTION === */
        .notes-section {
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          padding-top: 20px;
        }

        .notes-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .notes-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          color: rgba(148, 163, 184, 0.8);
        }

        .notes-title {
          font-size: 14px;
          font-weight: 500;
          color: rgba(226, 232, 240, 0.9);
        }

        .notes-time {
          font-size: 11px;
          color: rgba(100, 116, 139, 0.6);
        }

        .add-note-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          background: rgba(99, 102, 241, 0.08);
          border: 1px dashed rgba(99, 102, 241, 0.25);
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
          color: rgba(129, 140, 248, 0.9);
          cursor: pointer;
          transition: all 0.15s;
          margin-bottom: 16px;
        }

        .add-note-btn:hover {
          background: rgba(99, 102, 241, 0.12);
          border-color: rgba(99, 102, 241, 0.4);
          color: #818cf8;
        }

        /* === ADD NOTE FORM === */
        .add-note-form {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .note-title-input {
          width: 100%;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #fff;
          outline: none;
          margin-bottom: 10px;
        }

        .note-title-input::placeholder {
          color: rgba(100, 116, 139, 0.5);
        }

        .note-title-input:focus {
          border-color: rgba(99, 102, 241, 0.4);
        }

        .note-content-input {
          width: 100%;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 8px;
          font-size: 13px;
          color: rgba(226, 232, 240, 0.9);
          outline: none;
          resize: vertical;
          font-family: inherit;
          line-height: 1.5;
          margin-bottom: 12px;
        }

        .note-content-input::placeholder {
          color: rgba(100, 116, 139, 0.5);
        }

        .note-content-input:focus {
          border-color: rgba(99, 102, 241, 0.4);
        }

        .form-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .cancel-btn {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          color: rgba(148, 163, 184, 0.8);
          cursor: pointer;
          transition: all 0.15s;
        }

        .cancel-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .save-btn {
          padding: 8px 16px;
          background: #6366f1;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          color: #fff;
          cursor: pointer;
          transition: all 0.15s;
        }

        .save-btn:hover:not(:disabled) {
          background: #818cf8;
        }

        .save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* === NOTES LIST === */
        .notes-list-container {
          margin-top: 8px;
        }
      `}</style>
    </div>
  );
}

// === HELPER FUNCTION ===
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
